import React, { useState, useEffect } from 'react';
import { subscribeToNewsletter } from '../services/supabaseClient';
import './ComingSoonPage.css';

const ComingSoonPage = ({ mode = 'auto' }) => {
  const [timeLeft, setTimeLeft] = useState({
    days: 0,
    hours: 0,
    minutes: 0,
    seconds: 0
  });

  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState('');

  // Determine display mode: 'coming-soon' or 'maintenance'
  // 'auto' mode shows maintenance if we're past the launch date
  const targetDate = new Date('2026-02-20T12:00:00Z').getTime();
  const isMaintenanceMode = mode === 'maintenance' || (mode === 'auto' && Date.now() > targetDate);

  useEffect(() => {
    if (isMaintenanceMode) return; // No countdown in maintenance mode
    const timer = setInterval(() => {
      const now = Date.now();
      const distance = targetDate - now;
      if (distance > 0) {
        setTimeLeft({
          days: Math.floor(distance / (1000 * 60 * 60 * 24)),
          hours: Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60)),
          minutes: Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60)),
          seconds: Math.floor((distance % (1000 * 60)) / 1000)
        });
      } else {
        setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
      }
    }, 1000);
    return () => clearInterval(timer);
  }, [isMaintenanceMode, targetDate]);

  const handleNewsletterSubmit = async (e) => {
    e.preventDefault();
    if (!email.trim()) { setSubmitMessage('Please enter a valid email address.'); return; }
    setIsSubmitting(true); setSubmitMessage('');
    try {
      const result = await subscribeToNewsletter(email.trim());
      if (result.success) { setEmail(''); setSubmitMessage(result.message); }
      else setSubmitMessage(result.message);
    } catch { setSubmitMessage('An error occurred. Please try again.'); }
    finally { setIsSubmitting(false); }
  };

  if (isMaintenanceMode) {
    return (
      <div className="coming-soon-container">
        <div className="content">
          <div className="logo-section" style={{ marginBottom: '2rem' }}>
            <h1 className="main-title">The Orbital Ledger</h1>
          </div>

          {/* Status indicator bar */}
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: '0.6rem',
            background: 'rgba(255, 212, 71, 0.1)',
            border: '1px solid rgba(255, 212, 71, 0.3)',
            borderRadius: '24px',
            padding: '0.5rem 1.5rem',
            marginBottom: '2.5rem',
            fontFamily: "'Open Sans', sans-serif",
            fontSize: '0.85rem',
            color: '#FFD447',
            letterSpacing: '1.5px',
            textTransform: 'uppercase'
          }}>
            <span style={{
              display: 'inline-block',
              width: '8px',
              height: '8px',
              borderRadius: '50%',
              background: '#FFD447',
              animation: 'glow 1.5s ease-in-out infinite alternate',
              boxShadow: '0 0 8px rgba(255, 212, 71, 0.6)'
            }} />
            Maintenance in Progress
          </div>

          <div className="coming-soon-section" style={{ marginBottom: '3rem' }}>
            <h2 className="coming-soon-title" style={{ fontSize: '2.4rem', marginBottom: '1rem' }}>
              Quantum Drive Recalibrating
            </h2>
            <p className="description" style={{ maxWidth: '620px', margin: '0 auto', fontFamily: "'Open Sans', sans-serif", fontSize: '1.1rem', lineHeight: '1.8' }}>
              The Orbital Ledger is temporarily offline while our engineering crew
              performs critical system upgrades. We apologize for the inconvenience
              and appreciate your patience. All account data, trade records, and
              organization information remain safe and encrypted.
            </p>
          </div>

          {/* Status cards */}
          <div className="features-section" style={{ marginBottom: '3rem' }}>
            <div className="features-grid" style={{ maxWidth: '900px', margin: '0 auto', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))' }}>
              <div className="feature-card" style={{ borderColor: '#FFD447', borderWidth: '1px' }}>
                <div style={{ fontSize: '2.2rem', marginBottom: '0.8rem' }}>&#9881;&#65039;</div>
                <h4>System Upgrades</h4>
                <p>Deploying performance improvements and new features to enhance your experience</p>
              </div>
              <div className="feature-card" style={{ borderColor: '#22c55e', borderWidth: '1px' }}>
                <div style={{ fontSize: '2.2rem', marginBottom: '0.8rem' }}>&#128274;</div>
                <h4>Your Data is Safe</h4>
                <p>All accounts, trade history, inventories, and org data are securely stored and encrypted</p>
              </div>
              <div className="feature-card" style={{ borderColor: '#00D1D1', borderWidth: '1px' }}>
                <div style={{ fontSize: '2.2rem', marginBottom: '0.8rem' }}>&#128225;</div>
                <h4>Status Updates</h4>
                <p>Follow our Discord for real-time progress updates and estimated return time</p>
              </div>
              <div className="feature-card" style={{ borderColor: '#0d8fff', borderWidth: '1px' }}>
                <div style={{ fontSize: '2.2rem', marginBottom: '0.8rem' }}>&#9200;</div>
                <h4>Back Shortly</h4>
                <p>We expect to have all systems back online soon. Thank you for flying with us</p>
              </div>
            </div>
          </div>

          {/* Newsletter signup */}
          <div className="newsletter-section" style={{ maxWidth: '600px', margin: '0 auto 3rem' }}>
            <h3 style={{ fontSize: '1.4rem' }}>Get Notified When We Return</h3>
            <p style={{ fontSize: '1rem' }}>Enter your email and we will let you know the moment systems are back online.</p>
            <form onSubmit={handleNewsletterSubmit} className="newsletter-form">
              <input type="email" placeholder="citizen@email.com" className="email-input" value={email} onChange={(e) => setEmail(e.target.value)} disabled={isSubmitting} />
              <button type="submit" className="notify-button" disabled={isSubmitting}>{isSubmitting ? 'Subscribing...' : 'Notify Me'}</button>
            </form>
            {submitMessage && (
              <p style={{ color: submitMessage.includes('Successfully') ? '#4CAF50' : '#ffb347', marginTop: '8px', fontSize: '0.95em' }}>{submitMessage}</p>
            )}
          </div>

          <div className="footer">
            <p>&copy; 2026 The Orbital Ledger. Not affiliated with Cloud Imperium Games. All trademarks belong to their respective owners.</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="coming-soon-container">
      {/* Starfield background is global */}
      <div className="content">
        <div className="logo-section">
          <h1 className="main-title">The Orbital Ledger</h1>
          <div className="subtitle">Tools for Citizens who trade, explore, and build empires</div>
        </div>

        <div className="coming-soon-section">
          <h2 className="coming-soon-title">Launching Soon</h2>
          <p className="description">
            Calling all Citizens: chart smarter routes, track live commodity prices, coordinate org cargo, and grow your fortune across Stanton and beyond. The Orbital Ledger is your co-pilot for trade mastery in Star Citizen.
          </p>
        </div>

        <div className="countdown-section">
          <h3>Launch Countdown</h3>
          <div className="countdown-timer">
            <div className="countdown-item"><span className="number">{timeLeft.days}</span><span className="label">Days</span></div>
            <div className="countdown-item"><span className="number">{timeLeft.hours}</span><span className="label">Hours</span></div>
            <div className="countdown-item"><span className="number">{timeLeft.minutes}</span><span className="label">Minutes</span></div>
            <div className="countdown-item"><span className="number">{timeLeft.seconds}</span><span className="label">Seconds</span></div>
          </div>
        </div>

        <div className="features-section">
          <h3>Why Citizens Choose TOL</h3>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">&#128202;</div>
              <h4>Live Market Intel</h4>
              <p>Commodity price tracking across hubs to spot profit fast</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">&#128506;</div>
              <h4>Route Planning</h4>
              <p>Plan efficient loops and multi-stop cargo runs with ease</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">&#128101;</div>
              <h4>Org Command</h4>
              <p>Kanban-style cargo ops, seats, and shared goals for your org</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">&#128736;</div>
              <h4>Wikelo &amp; Hangar Tools</h4>
              <p>Curated contracts and item tracking for ships, armor, and weapons</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">&#128210;</div>
              <h4>Ledger</h4>
              <p>Track trades, profits, and org inventory across your fleet with export-ready logs.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">&#128640;</div>
              <h4>More Coming Soon</h4>
              <p>Org events, mission boards, and a mobile companion rolling out with the beta.</p>
            </div>
          </div>
        </div>

        <div className="newsletter-section">
          <h3>Join the Flight Roster</h3>
          <p>Get early access updates, patch news, and trading tips. No spam -- just signals that matter in the verse.</p>
          <form onSubmit={handleNewsletterSubmit} className="newsletter-form">
            <input type="email" placeholder="Enter your email address" className="email-input" value={email} onChange={(e) => setEmail(e.target.value)} disabled={isSubmitting} />
            <button type="submit" className="notify-button" disabled={isSubmitting}>{isSubmitting ? 'Subscribing...' : 'Notify Me'}</button>
          </form>
          {submitMessage && (
            <p style={{ color: submitMessage.includes('Successfully') ? '#4CAF50' : '#ffb347', marginTop: '8px', fontSize: '0.95em' }}>{submitMessage}</p>
          )}
        </div>

        <div className="footer">
          <p>&copy; 2026 The Orbital Ledger. Not affiliated with Cloud Imperium Games. All trademarks belong to their respective owners.</p>
        </div>
      </div>
    </div>
  );
};

export default ComingSoonPage; 